// onsite-test-dispatcher.js
// Central runner for all on-site edit A/B tests.
//
// How it works:
//   1. Reads window.__onsiteTestConfig (set by snippets/onsite-test-config.liquid) to get
//      the list of active tests, each with a slug, an Intelligems flag name, and a module URL.
//   2. Hooks into window.onIgReady (Intelligems' callback array) so it runs as soon as
//      Intelligems has resolved traffic assignments — no polling, no race conditions.
//   3. For each test, evaluate() checks:
//        a. If the QA toggle is enabled (window.__onsiteTestToggleEnabled), localStorage is
//           checked first for a manual override ("on" / "off").
//        b. Otherwise (or if no override), defers to the Intelligems window flag.
//   4. If a test is active, activateTest() adds a `test--{slug}` class to <body>, then
//      dynamically loads the test's JS module and calls its init() function.
//
// QA shortcut: if any test has a localStorage "on" override, the dispatcher also fires
// immediately on DOMContentLoaded (without waiting for Intelligems) so QA can work on
// themes where Intelligems isn't running.

(function () {
	const config = window.__onsiteTestConfig || [];
	if (!config.length) return;

	window.__onsiteTestModules = {};
	window.__onsiteTestsActive = {};

	const activated = {};

	function isLiveTheme() {
		return (
			typeof Shopify !== "undefined" &&
			Shopify.theme &&
			Shopify.theme.role === "main"
		);
	}

	function qaEnabled(test) {
		if (isLiveTheme()) return false;
		const role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
		const previewSlugs = window.__onsiteTestPreviewSlugs || [];
		return window.__onsiteTestToggleEnabled ||
			((role === "development" || role === "unpublished") && previewSlugs.includes(test.slug));
	}

	function resolvedVariant(test) {
		if (qaEnabled(test)) {
			try {
				const overrideState = localStorage.getItem(
					"__onsite_test__" + test.slug,
				);
				const override = localStorage.getItem(
					"__onsite_test__" + test.slug + "__variant",
				);
				if (override) return override;
				if (overrideState === "off" && test.always)
					return test.defaultVariant || "control";
			} catch (_) {}
		}
		return window[test.igFlag] || test.defaultVariant || "B";
	}

	function activateTest(test) {
		if (activated[test.slug]) return;
		activated[test.slug] = true;
		window.__onsiteTestsActive[test.slug] = true;

		document.body.classList.add("test--" + test.slug);

		if (!test.moduleSrc) return;

		const variant = resolvedVariant(test);

		const s = document.createElement("script");
		s.src = test.moduleSrc;
		s.onload = function () {
			const mod = window.__onsiteTestModules[test.slug];
			if (mod && mod.init) mod.init(variant);
		};
		document.head.appendChild(s);
	}

	function evaluate(test) {
		if (qaEnabled(test)) {
			const override = localStorage.getItem("__onsite_test__" + test.slug);
			if (override === "off") return !!test.always;
			if (override === "on") return true;
		}
		if (test.always) return true;
		return !!window[test.igFlag];
	}

	function run() {
		config.forEach(function (test) {
			if (evaluate(test)) activateTest(test);
		});
	}

	function runWhenBodyReady() {
		if (document.body) {
			run();
		} else {
			document.addEventListener("DOMContentLoaded", run);
		}
	}

	const hasQaVariantOverride = config.some(function (t) {
		if (!qaEnabled(t)) return false;
		try {
			return t.always || localStorage.getItem("__onsite_test__" + t.slug) === "on";
		} catch (_) { return false; }
	});

	if (hasQaVariantOverride) runWhenBodyReady();

	const existing = window.onIgReady;
	if (Array.isArray(existing)) {
		existing.push(runWhenBodyReady);
	} else if (typeof existing === "function") {
		window.onIgReady = [existing, runWhenBodyReady];
	} else {
		window.onIgReady = [runWhenBodyReady];
	}
})();
